import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from 'src/app/authentication/login/login.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { EmitirDeclaracoesService } from './emitir-declaracao.service';
import {
  DeclaracaoTacDialogComponent,
  DeclaracaoTacDialogData
} from './declaracao-tac-dialog/declaracao-tac-dialog.component';
import { CertidaoPreviewDialogComponent } from '../emitir-declaracao-pad/certidao-preview-dialog/certidao-preview-dialog.component';

const MENSAGEM_PENDENCIAS =
  'Servidor possui pendências, solicitar a Declaração no e-mail corregedoriageral@goias.gov.br';

@Component({
  selector: 'app-emitir-declaracao',
  templateUrl: './emitir-declaracao.component.html',
  styleUrls: ['./emitir-declaracao.component.css']
})
export class EmitirDeclaracaoComponent implements OnInit {
  servidorForm: UntypedFormGroup;
  emitirDisabled = true;
  consultaLoading = false;
  downloadLoading = false;

  constructor(
    private fb: UntypedFormBuilder,
    private emitirDeclaracoesService: EmitirDeclaracoesService,
    private snackBar: SnackBarService,
    private dialog: MatDialog,
    private loginService: LoginService
  ) {
    this.servidorForm = this.fb.group({
      id: [],
      nome: [{ value: null, disabled: true }, Validators.required],
      cpf: [{ value: null, disabled: true }]
    });
  }

  ngOnInit(): void {}

  buscarServidor(servidor: any): void {
    this.servidorForm.patchValue(servidor);
    this.emitirDisabled = !this.servidorForm.getRawValue().id;
  }

  consultar(): void {
    const raw = this.servidorForm.getRawValue();
    if (!raw.id) {
      this.snackBar.showSnackBarError('Selecione um servidor antes de consultar.');
      return;
    }
    this.consultaLoading = true;
    this.emitirDeclaracoesService.consultarSituacao(raw.id).subscribe({
      next: (res: any) => {
        this.consultaLoading = false;
        const resultado = res?.resultado || {};
        const possuiPendencias = !!resultado?.possui_pendencias;
        const emissaoCompleta = resultado?.emissaoCompleta ?? this.loginService.isDeclaracaoEmissaoCompleta();
        const dialogData: DeclaracaoTacDialogData = {
          dataConsulta: new Date(),
          cpf: this.formatarCpf(String(raw.cpf || '')),
          nome: raw.nome || '',
          situacao: possuiPendencias ? 'possui_pendencias' : 'nada_consta',
          servidorId: raw.id,
          podeEmitir: true,
          mensagemPendencias: possuiPendencias && !emissaoCompleta ? MENSAGEM_PENDENCIAS : undefined,
          emissaoCompleta: !!emissaoCompleta,
          tacs: resultado?.tacs || []
        };
        const dialogRef = this.dialog.open(DeclaracaoTacDialogComponent, {
          width: possuiPendencias ? '920px' : '680px',
          maxWidth: '95vw',
          data: dialogData,
          disableClose: false
        });
        dialogRef.afterClosed().subscribe((result?: {
          emitir: boolean;
          forcarNadaConsta?: boolean;
          observacoes?: string;
          tacsSelecionados?: string[];
        }) => {
          if (result?.emitir && raw.id) {
            this.abrirPreviewEDownload(raw.id, result.forcarNadaConsta, result.observacoes, result.tacsSelecionados);
          }
        });
      },
      error: (err) => {
        this.consultaLoading = false;
        this.snackBar.showSnackBarError(
          this.extrairMensagemErro(err, 'Falha ao consultar a situação do servidor.')
        );
      }
    });
  }

  private formatarCpf(cpf: string): string {
    const n = cpf.replace(/\D/g, '');
    if (n.length !== 11) return cpf;
    return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  private montarParams(
    servidorId: number,
    forcarNadaConsta?: boolean,
    observacoes?: string,
    tacsSelecionados?: string[]
  ): Record<string, unknown> {
    const params: Record<string, unknown> = { servidorId };
    if (forcarNadaConsta) params.forcarNadaConsta = true;
    if (observacoes) params.observacoes = observacoes;
    if (tacsSelecionados?.length) {
      params.tacsSelecionados = tacsSelecionados.join(',');
    }
    return params;
  }

  private abrirPreviewEDownload(
    servidorId: number,
    forcarNadaConsta?: boolean,
    observacoes?: string,
    tacsSelecionados?: string[]
  ): void {
    const servidorIdNumerico = Number(servidorId);
    if (!Number.isFinite(servidorIdNumerico) || servidorIdNumerico <= 0) {
      this.snackBar.showSnackBarError('Servidor inválido para emissão da declaração.');
      return;
    }

    this.downloadLoading = true;
    const params = this.montarParams(servidorIdNumerico, forcarNadaConsta, observacoes, tacsSelecionados);

    this.emitirDeclaracoesService.previewDeclaracao(params).subscribe({
      next: (htmlContent: any) => {
        this.downloadLoading = false;
        const previewRef = this.dialog.open(CertidaoPreviewDialogComponent, {
          width: '860px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          data: { htmlContent: typeof htmlContent === 'string' ? htmlContent : String(htmlContent) }
        });
        previewRef.afterClosed().subscribe((confirmar: boolean) => {
          if (confirmar) {
            this.executarDownload(servidorIdNumerico, forcarNadaConsta, observacoes, tacsSelecionados);
          }
        });
      },
      error: (error) => {
        console.error(error);
        this.downloadLoading = false;
        this.snackBar.showSnackBarError(
          this.extrairMensagemErro(error, 'Erro ao gerar pré-visualização da declaração.')
        );
      }
    });
  }

  private executarDownload(
    servidorId: number,
    forcarNadaConsta?: boolean,
    observacoes?: string,
    tacsSelecionados?: string[]
  ): void {
    this.downloadLoading = true;
    const params = this.montarParams(servidorId, forcarNadaConsta, observacoes, tacsSelecionados);

    this.emitirDeclaracoesService.downloadDeclaracao(params).subscribe({
      next: (declaracao: any) => {
        this.downloadLoading = false;
        const binaryData = atob(declaracao.pdf);
        const len = binaryData.length;
        const buffer = new ArrayBuffer(len);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < len; i++) {
          view[i] = binaryData.charCodeAt(i);
        }
        const pdfBlob = new Blob([view], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = declaracao.fileName;
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
          link.remove();
        }, 100);
      },
      error: (error) => {
        console.error(error);
        this.downloadLoading = false;
        this.snackBar.showSnackBarError(this.extrairMensagemErro(error, 'Erro ao gerar declaração.'));
      }
    });
  }

  private extrairMensagemErro(err: any, fallback: string): string {
    return err?.error?.erro || err?.error?.message || err?.message || fallback;
  }
}
